import React from 'react';
import { Navigate } from 'react-router-dom';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { LoadingSpinner } from '../common/LoadingSpinner';

interface GroupProtectedRouteProps {
  requiredGroup: string;
  /** Additional groups that also grant access (e.g. an admin group; admin implies member). */
  allowGroups?: string[];
  children: React.ReactNode;
}

export const GroupProtectedRoute: React.FC<GroupProtectedRouteProps> = ({
  requiredGroup,
  allowGroups = [],
  children,
}) => {
  const isAuthenticated = useIsAuthenticated();
  const { accounts, inProgress } = useMsal();

  // On a full-page reload this guard renders before MSAL has finished starting up / processing a
  // pending redirect. Deciding now would see an authenticated user as logged out and bounce them
  // (→ /login → /), losing the deep link. Wait for MSAL to settle before deciding — same as
  // ProtectedRoute's MsalAuthenticationTemplate does for the portal home.
  if (inProgress !== InteractionStatus.None) {
    return <LoadingSpinner message="Loading…" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const groups: string[] =
    (accounts[0]?.idTokenClaims as Record<string, unknown>)?.groups as string[] ?? [];

  const allowed = [requiredGroup, ...allowGroups];
  if (!allowed.some((g) => g && groups.includes(g))) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
